import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { App, HealthcareProviderInfo, InviteCode, Nullable, PostcodesAccessRule, SpeechNeed, SpeechNeedsState, User } from '../interfaces';
import { PresentedData, PresentedView, Presenter } from './Presentation';
import { hasDashboardFeature, isRootOrSuperRole } from '../DashboardAccess';

export class SpeechNeedsPresenter implements Presenter {
    private app: App;
    private view: Nullable<PresentedView>;
    private healthcareProviderId!: string;
    private healthcareProviderInfo: HealthcareProviderInfo = {
        id: '',
        name: '',
        inviteCodes: [],
        dashboardUrl: '',
        postcodesAccess: [],
    };
    private speechNeeds: Array<SpeechNeed> = [];
    private allSpeechNeeds: Array<SpeechNeed> = [];
    private authenticatedUser?: User;
    private activationKeyError: string | null = null;
    private activationKeyOperationInProgress = false;

    private requireDevelopmentAccess() {
        // Repeat the UI entitlement check at every development write entry point.
        const dashboardRole = this.authenticatedUser?.dashboardRole || 'unauthenticated';
        if (!hasDashboardFeature(this.healthcareProviderInfo, dashboardRole, 'development')) {
            throw new Error('Your role does not have access to development features.');
        }
    }

    private requireActivationKeyAccess() {
        if (!this.canManageActivationKeys()) {
            throw new Error(
                this.healthcareProviderInfo.commercialModel === 'self_service'
                    ? 'Only provider Admins, Root, or Super users can manage activation keys.'
                    : 'Only Root or Super users can manage activation keys.'
            );
        }
    }

    private canManageActivationKeys(): boolean {
        const dashboardRole = this.authenticatedUser?.dashboardRole || 'unauthenticated';
        return isRootOrSuperRole(dashboardRole) || (
            this.healthcareProviderInfo.commercialModel === 'self_service' &&
            dashboardRole === 'admin'
        );
    }

    private shouldUseSelfServiceActivationKeyApi(): boolean {
        // Platform support users use the same audited server path for SaaS providers;
        // their existing direct Enterprise/NHS workflow remains unchanged.
        return this.healthcareProviderInfo.commercialModel === 'self_service' &&
            this.canManageActivationKeys();
    }

    private setActivationKeyOperationState(inProgress: boolean, error: string | null = null) {
        this.activationKeyOperationInProgress = inProgress;
        this.activationKeyError = error;
        this.safeUpdatePresentedData();
    }

    private activationKeyErrorMessage(error: unknown, fallback: string): string {
        // Firebase callables sometimes replace the server error with an opaque placeholder.
        const message = error instanceof Error
            ? error.message.trim()
            : error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
                ? error.message.trim()
                : '';
        const normalizedMessage = message.toLowerCase();
        const isGenericCallableMessage = normalizedMessage === 'internal' ||
            normalizedMessage === 'unknown' ||
            normalizedMessage === 'functions/internal' ||
            normalizedMessage === 'functions/unknown' ||
            /^firebase: error \(functions\/(?:internal|unknown)\)\.?$/.test(normalizedMessage);

        return message && !isGenericCallableMessage ? message : fallback;
    }

    private inviteCodeFromCallableResponse(data: unknown): InviteCode {
        const response = data && typeof data === 'object' ? data as Record<string, unknown> : {};
        const value = response.inviteCode && typeof response.inviteCode === 'object'
            ? response.inviteCode as Record<string, unknown>
            : {};
        const code = typeof value.code === 'string' ? value.code.trim().toUpperCase() : '';
        const speechNeeds = Array.isArray(value.speechNeeds)
            ? value.speechNeeds.filter((speechNeed): speechNeed is string => typeof speechNeed === 'string' && !!speechNeed)
            : [];
        const legacySpeechNeed = typeof value.speechNeed === 'string' ? value.speechNeed : '';
        if (speechNeeds.length === 0 && legacySpeechNeed) {
            speechNeeds.push(legacySpeechNeed);
        }
        if (!code || value.createdAt === undefined) {
            throw new Error('The activation-key service returned an invalid response.');
        }
        return {
            code,
            speechNeed: speechNeeds[0] || legacySpeechNeed,
            speechNeeds,
            createdAt: value.createdAt,
            purpose: typeof value.purpose === 'string' ? value.purpose : undefined,
        };
    }

    private replaceLocalInviteCode(inviteCode: InviteCode) {
        // The callable response is authoritative; replace a stale row or append a newly created key.
        const inviteCodes = this.healthcareProviderInfo.inviteCodes || [];
        const existingIndex = inviteCodes.findIndex(existingInviteCode =>
            existingInviteCode.code.toUpperCase() === inviteCode.code.toUpperCase()
        );
        const nextInviteCodes = existingIndex >= 0
            ? inviteCodes.map((existingInviteCode, index) => index === existingIndex ? inviteCode : existingInviteCode)
            : [...inviteCodes, inviteCode];
        this.healthcareProviderInfo = {
            ...this.healthcareProviderInfo,
            inviteCodes: nextInviteCodes,
        };
    }

    constructor(options: { app: App, healthcareProviderId: string }) {
        const { app, healthcareProviderId } = options;
        if (!app) {
            throw new Error('SpeechNeedsPresenter(): missing app parameter');
        }
        this.app = app;
        this.healthcareProviderId = healthcareProviderId;
        this.observeAuthenticatedUser();
        this.observeHealthcareProviders();
        this.observeSpeechNeeds();
        this.observeAllSpeechNeeds();
    }

    private observeAuthenticatedUser() {
        this.app.queryUser().observe({
            changed: (user: User) => {
                this.authenticatedUser = user;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeHealthcareProviders() {
        console.log('SPEECH_NEEDS_PRESENTER: observeHealthcareProviders()');
        this.app.getData().queryHealthcareProviderInfo().observe({
            changed: (healthcareProviderInfo: HealthcareProviderInfo) => {
                console.log('SPEECH_NEEDS_PRESENTER: queryHealthcareProviderInfo().observe()', healthcareProviderInfo);
                this.healthcareProviderInfo = healthcareProviderInfo;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeSpeechNeeds() {
        console.log('SPEECH_NEEDS_PRESENTER: observeSpeechNeeds()');
        this.app.getData().querySpeechNeeds().observe({
            changed: (speechNeeds: SpeechNeedsState) => {
                console.log('SPEECH_NEEDS_PRESENTER: querySpeechNeeds().observe()', speechNeeds);
                this.speechNeeds = speechNeeds.speechNeeds;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeAllSpeechNeeds() {
        console.log('SPEECH_NEEDS_PRESENTER: observeAllSpeechNeeds()');
        // The full catalogue backs the access editor, which must offer support areas
        // the provider does not currently have.
        this.app.getData().queryAllSpeechNeeds().observe({
            changed: (speechNeeds: SpeechNeedsState) => {
                console.log('SPEECH_NEEDS_PRESENTER: queryAllSpeechNeeds().observe()', speechNeeds);
                this.allSpeechNeeds = speechNeeds.speechNeeds;
                this.safeUpdatePresentedData();
            }
        });
    }

    setHealthcareProviderId(healthcareProviderId: string) {
        this.healthcareProviderId = healthcareProviderId;
        console.log('SPEECH_NEEDS_PRESENTER: setHealthcareProviderId() healthcareProviderId:', this.healthcareProviderId);
        this.safeUpdatePresentedData();
    }

    private updateHealthcareProviderInfo() {
        // Save provider-level support-area settings from the latest local snapshot.
        this.app.getData().updateHealthcareProvider({
            id: this.healthcareProviderId,
            path: this.healthcareProviderInfo.path,
            name: this.healthcareProviderInfo.name,
            inviteCodes: this.healthcareProviderInfo.inviteCodes,
            dashboardUrl: this.healthcareProviderInfo.dashboardUrl,
            postcodesAccess: this.healthcareProviderInfo.postcodesAccess || [],
            reportTemplates: this.healthcareProviderInfo.reportTemplates,
        });
    }

    private async onCreateInviteCode(speechNeedIds: string[], purpose: string): Promise<boolean> {
        this.requireActivationKeyAccess();
        console.log('SPEECH_NEEDS_PRESENTER: onCreateInviteCode() speechNeedIds:', speechNeedIds, 'purpose:', purpose);
        const speechNeeds = speechNeedIds.filter(speechNeedId => speechNeedId);

        if (this.shouldUseSelfServiceActivationKeyApi()) {
            if (this.activationKeyOperationInProgress) {
                return false;
            }
            this.setActivationKeyOperationState(true);
            try {
                const createActivationKey = httpsCallable<{
                    healthcareProviderId: string;
                    speechNeed: string;
                    speechNeeds: string[];
                    purpose: string;
                }, unknown>(getFunctions(), 'createProviderActivationKey');
                const result = await createActivationKey({
                    healthcareProviderId: this.healthcareProviderId,
                    speechNeed: speechNeeds[0] || '',
                    speechNeeds,
                    purpose,
                });
                this.replaceLocalInviteCode(this.inviteCodeFromCallableResponse(result.data));
                this.setActivationKeyOperationState(false);
                return true;
            } catch (error) {
                this.setActivationKeyOperationState(
                    false,
                    this.activationKeyErrorMessage(error, 'Could not create the activation key.')
                );
                return false;
            }
        }

        // First, we need to create a new invite code, which is a 6 character string made up of upper case letters and numbers.
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();

        // Then, we need to create a new invite code object with current and legacy speech need fields.
        const inviteCode: InviteCode = {
            code,
            speechNeed: speechNeeds[0] || '',
            speechNeeds,
            purpose,
            createdAt: Timestamp.now(),
        };

        // Then, we need to add the invite code to the healthcare provider info.
        this.healthcareProviderInfo.inviteCodes.push(inviteCode);

        // Then, we need to update the healthcare provider info in the database.
        this.updateHealthcareProviderInfo();

        this.safeUpdatePresentedData();
        return true;
    }

    private async onDeleteInviteCode(inviteCode: InviteCode): Promise<boolean> {
        this.requireActivationKeyAccess();
        console.log('SPEECH_NEEDS_PRESENTER: onDeleteInviteCode() inviteCode:', inviteCode);

        if (this.shouldUseSelfServiceActivationKeyApi()) {
            if (this.activationKeyOperationInProgress) {
                return false;
            }
            this.setActivationKeyOperationState(true);
            try {
                const deleteActivationKey = httpsCallable<{
                    healthcareProviderId: string;
                    code: string;
                }, unknown>(getFunctions(), 'deleteProviderActivationKey');
                const result = await deleteActivationKey({
                    healthcareProviderId: this.healthcareProviderId,
                    code: inviteCode.code,
                });
                const response = result.data && typeof result.data === 'object'
                    ? result.data as Record<string, unknown>
                    : {};
                const deletedCode = typeof response.code === 'string' ? response.code.toUpperCase() : '';
                if (response.deleted !== true || deletedCode !== inviteCode.code.toUpperCase()) {
                    throw new Error('The activation-key service returned an invalid response.');
                }
                this.healthcareProviderInfo = {
                    ...this.healthcareProviderInfo,
                    inviteCodes: this.healthcareProviderInfo.inviteCodes.filter(existingInviteCode =>
                        existingInviteCode.code.toUpperCase() !== deletedCode
                    ),
                };
                this.setActivationKeyOperationState(false);
                return true;
            } catch (error) {
                this.setActivationKeyOperationState(
                    false,
                    this.activationKeyErrorMessage(error, 'Could not delete the activation key.')
                );
                return false;
            }
        }

        this.healthcareProviderInfo.inviteCodes = this.healthcareProviderInfo.inviteCodes.filter(ic => ic.code !== inviteCode.code);
        this.updateHealthcareProviderInfo();
        this.safeUpdatePresentedData();
        return true;
    }

    private async onEditInviteCode(inviteCode: InviteCode): Promise<boolean> {
        this.requireActivationKeyAccess();
        console.log('SPEECH_NEEDS_PRESENTER: onEditInviteCode() inviteCode:', inviteCode);

        // Keep the new multi-select field and legacy single-select field in sync.
        const speechNeeds = (inviteCode.speechNeeds && inviteCode.speechNeeds.length > 0
            ? inviteCode.speechNeeds
            : [inviteCode.speechNeed]
        ).filter(speechNeedId => speechNeedId);
        const updatedInviteCode: InviteCode = {
            ...inviteCode,
            speechNeed: speechNeeds[0] || '',
            speechNeeds,
        };

        if (this.shouldUseSelfServiceActivationKeyApi()) {
            if (this.activationKeyOperationInProgress) {
                return false;
            }
            this.setActivationKeyOperationState(true);
            try {
                const updateActivationKey = httpsCallable<{
                    healthcareProviderId: string;
                    code: string;
                    speechNeed: string;
                    speechNeeds: string[];
                    purpose: string;
                }, unknown>(getFunctions(), 'updateProviderActivationKey');
                const result = await updateActivationKey({
                    healthcareProviderId: this.healthcareProviderId,
                    code: inviteCode.code,
                    speechNeed: updatedInviteCode.speechNeed,
                    speechNeeds,
                    purpose: updatedInviteCode.purpose || '',
                });
                this.replaceLocalInviteCode(this.inviteCodeFromCallableResponse(result.data));
                this.setActivationKeyOperationState(false);
                return true;
            } catch (error) {
                this.setActivationKeyOperationState(
                    false,
                    this.activationKeyErrorMessage(error, 'Could not update the activation key.')
                );
                return false;
            }
        }

        // Replace the matching activation key row while preserving the key code and creation date.
        this.healthcareProviderInfo.inviteCodes = this.healthcareProviderInfo.inviteCodes.map(existingInviteCode =>
            existingInviteCode.code === inviteCode.code ? updatedInviteCode : existingInviteCode
        );

        this.updateHealthcareProviderInfo();
        this.safeUpdatePresentedData();
        return true;
    }

    private normalisePostcodesAccessRule(postcodesAccessRule: PostcodesAccessRule): PostcodesAccessRule {
        // Keep postcode rule storage compact and strip blank support-area ids.
        return {
            rule: postcodesAccessRule.rule.trim(),
            speechNeeds: postcodesAccessRule.speechNeeds.filter(speechNeedId => speechNeedId),
        };
    }

    private onCreatePostcodesAccessRule(postcodesAccessRule: PostcodesAccessRule) {
        this.requireDevelopmentAccess();
        console.log('SPEECH_NEEDS_PRESENTER: onCreatePostcodesAccessRule() postcodesAccessRule:', postcodesAccessRule);

        // Append the new postcode rule to the provider's access list.
        const nextPostcodesAccessRule = this.normalisePostcodesAccessRule(postcodesAccessRule);
        this.healthcareProviderInfo.postcodesAccess = [
            ...(this.healthcareProviderInfo.postcodesAccess || []),
            nextPostcodesAccessRule,
        ];

        this.updateHealthcareProviderInfo();
        this.safeUpdatePresentedData();
    }

    private onEditPostcodesAccessRule(index: number, postcodesAccessRule: PostcodesAccessRule) {
        this.requireDevelopmentAccess();
        console.log('SPEECH_NEEDS_PRESENTER: onEditPostcodesAccessRule() index:', index, 'postcodesAccessRule:', postcodesAccessRule);

        // Replace the row by index because postcode access entries do not have stable ids.
        const currentPostcodesAccess = this.healthcareProviderInfo.postcodesAccess || [];
        if (index < 0 || index >= currentPostcodesAccess.length) {
            return;
        }

        const nextPostcodesAccessRule = this.normalisePostcodesAccessRule(postcodesAccessRule);
        this.healthcareProviderInfo.postcodesAccess = currentPostcodesAccess.map((existingPostcodesAccessRule, existingIndex) =>
            existingIndex === index ? nextPostcodesAccessRule : existingPostcodesAccessRule
        );

        this.updateHealthcareProviderInfo();
        this.safeUpdatePresentedData();
    }

    private onSaveProviderSpeechNeeds(speechNeedIds: string[]) {
        this.requireDevelopmentAccess();
        console.log('SPEECH_NEEDS_PRESENTER: onSaveProviderSpeechNeeds() speechNeedIds:', speechNeedIds);

        // Always store the explicit selection, so the panel's default for
        // unconfigured providers never masquerades as a saved access list.
        this.healthcareProviderInfo.speechNeeds = speechNeedIds;
        this.app.getData().updateHealthcareProviderSpeechNeeds(this.healthcareProviderId, speechNeedIds);
        this.safeUpdatePresentedData();
    }

    private onDeletePostcodesAccessRule(index: number) {
        this.requireDevelopmentAccess();
        console.log('SPEECH_NEEDS_PRESENTER: onDeletePostcodesAccessRule() index:', index);

        // Remove the selected postcode rule by its current table position.
        this.healthcareProviderInfo.postcodesAccess = (this.healthcareProviderInfo.postcodesAccess || [])
            .filter((_, existingIndex) => existingIndex !== index);

        this.updateHealthcareProviderInfo();
        this.safeUpdatePresentedData();
    }

    presentedData(): PresentedData {
        const dashboardRole = this.authenticatedUser?.dashboardRole || 'unauthenticated';
        return {
            authenticatedUserType: this.authenticatedUser?.type || 'therapist',
            dashboardRole,
            canUseDevelopmentFeatures: hasDashboardFeature(
                this.healthcareProviderInfo,
                dashboardRole,
                'development'
            ),
            canManageInviteCodes: this.canManageActivationKeys(),
            activationKeyError: this.activationKeyError,
            activationKeyOperationInProgress: this.activationKeyOperationInProgress,
            inviteCodes: this.healthcareProviderInfo.inviteCodes,
            postcodesAccess: this.healthcareProviderInfo.postcodesAccess || [],
            speechNeeds: this.speechNeeds,
            allSpeechNeeds: this.allSpeechNeeds,
            providerCommercialModel: this.healthcareProviderInfo.commercialModel,
            providerSpeechNeeds: this.healthcareProviderInfo.speechNeeds,
            onSaveProviderSpeechNeeds: (speechNeedIds: string[]) => { this.onSaveProviderSpeechNeeds(speechNeedIds); },
            onCreateInviteCode: (speechNeedIds: string[], purpose: string) => this.onCreateInviteCode(speechNeedIds, purpose),
            onDeleteInviteCode: (inviteCode: InviteCode) => this.onDeleteInviteCode(inviteCode),
            onEditInviteCode: (inviteCode: InviteCode) => this.onEditInviteCode(inviteCode),
            onCreatePostcodesAccessRule: (postcodesAccessRule: PostcodesAccessRule) => { this.onCreatePostcodesAccessRule(postcodesAccessRule); },
            onEditPostcodesAccessRule: (index: number, postcodesAccessRule: PostcodesAccessRule) => { this.onEditPostcodesAccessRule(index, postcodesAccessRule); },
            onDeletePostcodesAccessRule: (index: number) => { this.onDeletePostcodesAccessRule(index); },
        };
    }


    viewWillUpdate(view: PresentedView): void {
        this.view = view;
    }

    viewWillNotUpdate(/*view: PresentedView*/) {
        this.view = null;
    }

    private safeUpdatePresentedData() {
        const data = this.presentedData();
        if (this.view) {
            this.view.updatePresentedData(data);
        }
    }
}
